/**
 * Cài đặt › Module › Tiện ích (ADR-025 §4.6, §8): the add-ons of the build, their lifecycle, the
 * events they have not handled and their own pages (subsystems/platform/facade/adapters/admin-api/src/admin/addons.rs). No
 * add-on code runs in admin-api: a lifecycle change is recorded, the privileged storage steps run
 * on the next deploy, and every page block and action is forwarded to the add-on worker.
 */

import type { CallOptions, ISODateTime, WriteOptions } from '../types';
import type { IntegrationEnvironment } from './integrations';
import type { BilingualText, ModuleField, ModuleManifest } from './modules';

/** `platform_addons::lifecycle::AddonState`. */
export const ADDON_STATES = ['NOT_INSTALLED', 'INSTALL_PENDING', 'INSTALLED', 'ACTIVE', 'INACTIVE', 'DEGRADED', 'UNINSTALL_PENDING', 'UNINSTALLED'] as const;
export type AddonState = (typeof ADDON_STATES)[number];

/** What staff may ask (`Request::as_str`, upgrades run by themselves on deploy). */
export const ADDON_REQUESTS = ['install', 'activate', 'deactivate', 'uninstall'] as const;
export type AddonRequestKind = (typeof ADDON_REQUESTS)[number];

/** What an uninstall does with the add-on's data (`Retention::as_str`). */
export const ADDON_RETENTIONS = ['KEEP_DATA', 'EXPORT_THEN_DROP', 'DROP'] as const;
export type AddonRetention = (typeof ADDON_RETENTIONS)[number];

/** An event the add-on has not handled (`deliveries::WaitingStatus`). */
export const ADDON_WAITING_STATUSES = ['RETRYING', 'PARKED'] as const;
export type AddonWaitingStatus = (typeof ADDON_WAITING_STATUSES)[number];

/** How a table column of an add-on page is shown (`ui::ColumnKind`). */
export const ADDON_COLUMN_KINDS = ['text', 'number', 'date', 'date_time', 'domain', 'status', 'vnd'] as const;
export type AddonColumnKind = (typeof ADDON_COLUMN_KINDS)[number];

/** The Control Center hub an add-on's menu entry belongs to (`ui::MenuSection`). */
export const ADDON_MENU_SECTIONS = ['domains', 'customers', 'sales', 'products', 'compliance', 'ops', 'settings'] as const;
export type AddonMenuSection = (typeof ADDON_MENU_SECTIONS)[number];

/** A request possible now, and whether a second person approves it first. */
export interface AddonRequestOption {
  request: AddonRequestKind;
  four_eyes: boolean;
  /** Uninstall only: "delete the data now" always needs a second person. */
  drop_four_eyes?: boolean;
}

export interface AddonRetentionChoice {
  kind: AddonRetention;
  /** EXPORT_THEN_DROP: how many days the export is kept. */
  days?: number;
}

/** `settings.addons`: the add-on's lifecycle row. */
export interface AddonRecord {
  /** The add-on's version whose storage steps ran. */
  version: string | null;
  schema_version: number;
  retention: AddonRetentionChoice | null;
  /** EXPORT_THEN_DROP: when the encrypted export is deleted. */
  export_until: ISODateTime | null;
  /** DEGRADED: what made it stop (the add-on's own words, shortened). */
  degraded_reason: string | null;
  updated_at: ISODateTime;
}

/** A lifecycle change waiting for, or holding, a second person's approval. */
export interface AddonApproval {
  id: string;
  status: 'PENDING' | 'APPROVED';
  request: AddonRequestKind;
  retention: AddonRetention | null;
  retention_days: number | null;
  /** The caller asked for it (someone else approves it). */
  requested_by_self: boolean;
  expires_at: ISODateTime;
}

export interface AddonItem {
  manifest: ModuleManifest;
  state: AddonState;
  record: AddonRecord | null;
  requests: AddonRequestOption[];
  /** False on the production server for an add-on made for tests only. */
  installable_here: boolean;
  approval: AddonApproval | null;
  waiting: { parked: number; retrying: number };
  /** Its settings (the module instance `addons/<id>/default`). */
  settings: { exists: boolean; in_force: boolean };
}

/** A row of an add-on this build no longer has (its data may still be there). */
export interface AddonNotInBuild {
  addon: string;
  state: AddonState;
  record: AddonRecord;
}

export interface AddonOverview {
  environment: IntegrationEnvironment;
  can_manage: boolean;
  can_operate: boolean;
  /** api and admin-api can reach the add-on worker (pages, actions, routes). */
  worker_configured: boolean;
  items: AddonItem[];
  not_in_build: AddonNotInBuild[];
}

export interface AddonLifecycleBody {
  request: AddonRequestKind;
  retention?: AddonRetention;
  retention_days?: number;
  reason: string;
  /** The approved approval this change applies (and uses up). */
  approval_request_id?: string;
}

export type AddonLifecycleResult =
  | { result: 'APPLIED'; state: AddonState; storage_step: boolean; approval_request_id: string | null }
  | { result: 'APPROVAL_REQUESTED'; approval_request_id: string };

export interface AddonWaitingEvent {
  event_id: string;
  /** `registrar.<aggregate>.<event>.v<n>`. */
  subject: string;
  aggregate_id: string;
  occurred_at: ISODateTime;
  status: AddonWaitingStatus;
  attempts: number;
  next_attempt_at: ISODateTime | null;
  /** The add-on's own words about the last failure (shortened). */
  reason: string | null;
  first_attempt_at: ISODateTime;
  last_attempt_at: ISODateTime;
  parked_at: ISODateTime | null;
  replayed_at: ISODateTime | null;
}

// ------------------------------------------------------------------- pages (ui::AdminPage, Block)

export interface AddonColumn {
  key: string;
  label: BilingualText;
  kind: AddonColumnKind;
}

export type AddonBlock =
  | { kind: 'table'; key: string; columns: AddonColumn[]; filters: { fields: ModuleField[] } }
  | { kind: 'form'; key: string; schema: { fields: ModuleField[] }; action: string }
  | { kind: 'stat'; key: string; label: BilingualText }
  | { kind: 'text'; body: BilingualText }
  | { kind: 'button'; action: string; label: BilingualText; confirm: BilingualText | null };

export interface AddonAdminPage {
  key: string;
  title: BilingualText;
  permission: string;
  blocks: AddonBlock[];
}

export interface AddonMenuEntry {
  key: string;
  label: BilingualText;
  section: AddonMenuSection;
  page: string;
  permission: string;
}

/**
 * A menu entry of a running add-on for the main navigation (`GET /addon-menu`): only entries
 * whose permission (and their page's) the caller holds, only while the add-on is switched on.
 */
export interface AddonMenuItem extends AddonMenuEntry {
  /** The add-on's id. */
  addon: string;
  /** The add-on's name (its manifest's). */
  addon_name: BilingualText;
}

/** The pages the caller may open, as the add-on worker describes them. */
export interface AddonPages {
  addon: string;
  state: AddonState;
  version: string;
  menu: AddonMenuEntry[];
  pages: AddonAdminPage[];
  can_operate: boolean;
}

/** A block's data (`ui::BlockData`). */
export type AddonBlockData =
  | { kind: 'table'; rows: Record<string, unknown>[]; next: string | null }
  | { kind: 'stat'; value: unknown; hint: string | null };

/** What an action did (`ui::ActionResult`): the add-on's message; `reload`: the blocks again. */
export interface AddonActionResult {
  message: string;
  reload: boolean;
}

export interface AddonsApi {
  getAddons(opts?: CallOptions): Promise<AddonOverview>;
  requestAddonChange(addon: string, body: AddonLifecycleBody, opts: WriteOptions): Promise<AddonLifecycleResult>;
  listAddonEvents(addon: string, query: { status?: AddonWaitingStatus; limit?: number }, opts?: CallOptions): Promise<{ items: AddonWaitingEvent[] }>;
  replayAddonEvent(addon: string, eventId: string, body: { reason: string }, opts: WriteOptions): Promise<{ result: 'REPLAYED'; event_id: string }>;
  getAddonPages(addon: string, opts?: CallOptions): Promise<AddonPages>;
  /** The running add-ons' menu entries the caller may open (the main navigation, "Tiện ích"). */
  getAddonMenu(opts?: CallOptions): Promise<{ items: AddonMenuItem[] }>;
  queryAddonBlock(addon: string, body: { block: string; params: Record<string, unknown> }, opts?: CallOptions): Promise<AddonBlockData>;
  runAddonAction(addon: string, action: string, body: { input: Record<string, unknown> }, opts: WriteOptions): Promise<AddonActionResult>;
}
